import {useCallback, useEffect, useRef, useState} from 'react';
import type {UserMemories, UserMemoryFact, UserMemoryVisualEntity} from '@AiChat/ai-chat.types';
import {EMPTY_USER_MEMORIES, MemoryLoadState} from '@AiChat/ai-chat.types';
import {deleteUserMemoryFact, deleteUserMemoryVisualEntity, getUserMemories} from '@AiChat/lib/ai-chat-memory-library';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {openErrorModal} from '@Modals/error-modal';

interface UseUserMemoriesReturn {
  memories: UserMemories;
  loadState: MemoryLoadState;
  forgetFact: (fact: UserMemoryFact) => void;
  forgetVisualEntity: (entity: UserMemoryVisualEntity) => void;
}

/**
 * Owns the memory library's data from the provider, so it outlives the view: leaving for quick tips
 * and coming back shows what was already read instead of skeletons, and re-reads quietly behind it.
 */
export function useUserMemories(isMemoryView: boolean): UseUserMemoriesReturn {
  const [memories, setMemories] = useState<UserMemories>(EMPTY_USER_MEMORIES);
  const [loadState, setLoadState] = useState<MemoryLoadState>(MemoryLoadState.LOADING);
  const hasLoadedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isMemoryView) {
      return;
    }

    let isCurrent = true;

    if (!hasLoadedRef.current) {
      setLoadState(MemoryLoadState.LOADING);
    }

    void (async (): Promise<void> => {
      try {
        const storedMemories = await getUserMemories();
        if (!isCurrent) {
          return;
        }
        hasLoadedRef.current = true;
        setMemories(storedMemories);
        setLoadState(MemoryLoadState.LOADED);
      } catch (e) {
        console.error(e);
        // A re-read that fails leaves the memories we already showed standing; only a first read has nothing to fall back on.
        if (!isCurrent || hasLoadedRef.current) {
          return;
        }
        setLoadState(MemoryLoadState.FAILED);
      }
    })();

    return (): void => {
      isCurrent = false;
    };
  }, [isMemoryView]);

  /** Pruned locally rather than refetched: one confirmed delete should not repaint the library. */
  const forgetFact = useCallback((fact: UserMemoryFact): void => {
    void (async (): Promise<void> => {
      try {
        await deleteUserMemoryFact(fact.id);
        trackGA4CustomEvent(GA4EventName.AICHAT_MEMORY_REMOVE);
        setMemories((previous) => ({...previous, facts: previous.facts.filter((storedFact) => storedFact.id !== fact.id)}));
      } catch (e) {
        console.error(e);
        openErrorModal();
      }
    })();
  }, []);

  const forgetVisualEntity = useCallback((entity: UserMemoryVisualEntity): void => {
    void (async (): Promise<void> => {
      try {
        await deleteUserMemoryVisualEntity(entity.id);
        trackGA4CustomEvent(GA4EventName.AICHAT_MEMORY_REMOVE);
        setMemories((previous) => ({...previous, visualEntities: previous.visualEntities.filter((storedEntity) => storedEntity.id !== entity.id)}));
      } catch (e) {
        console.error(e);
        openErrorModal();
      }
    })();
  }, []);

  return {memories, loadState, forgetFact, forgetVisualEntity};
}
